import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { ButtonLink } from '../ui';
import { DURATION, EASE_OUT_EXPO } from '../../lib/motion';

const PROMPT = 'Хочу 11 ночей по югу Турции на машине: море, античность, без спешки';

const GENERATED = [
  { day: 1, title: 'Прилёт, аренда авто, Олюдениз', tag: 'Даламан → Олюдениз · 50 км' },
  { day: 2, title: 'Тлос, каньон Саклыкент, Пинара', tag: 'круговой маршрут по долине' },
  { day: 4, title: 'Переезд в Каш через Калкан и Капуташ', tag: '110 км · весь день' },
  { day: 5, title: 'Каякинг над затонувшим городом Кекова', tag: 'ранний старт, 4 часа на воде' },
  { day: 7, title: 'Вечные огни Химеры после заката', tag: 'подъём 30 мин · брать фонарь' },
];

/**
 * AI section.
 *
 * A self-running demo: the prompt types itself, then the plan assembles line by
 * line. It starts only when the section is actually on screen, and never runs at
 * all under reduced motion — the finished state is shown instead.
 */
export function AiSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduced = useReducedMotion();

  const [typed, setTyped] = useState(reduced ? PROMPT : '');
  const [visibleDays, setVisibleDays] = useState(reduced ? GENERATED.length : 0);
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    if (!inView || reduced) return;
    let index = 0;
    const typer = setInterval(() => {
      index += 1;
      setTyped(PROMPT.slice(0, index));
      if (index >= PROMPT.length) {
        clearInterval(typer);
        setThinking(true);
        setTimeout(() => {
          setThinking(false);
          let day = 0;
          const revealer = setInterval(() => {
            day += 1;
            setVisibleDays(day);
            if (day >= GENERATED.length) clearInterval(revealer);
          }, 320);
        }, 1100);
      }
    }, 34);
    return () => clearInterval(typer);
  }, [inView, reduced]);

  return (
    <section id="ai" className="grain relative isolate overflow-hidden bg-ink-950 py-24 text-sand-100 md:py-36">
      <div className="aurora opacity-25" />
      <div className="container-page relative">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="max-w-xl">
              <div className="eyebrow mb-4 text-sand-400">AI-планировщик</div>
              <h2 className="display text-4xl sm:text-5xl lg:text-[3.5rem]">
                Опишите поездку словами — получите маршрут
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-sand-300">
                Travessa разбирает запрос на части и собирает план в три захода: каркас по дням,
                точки на карте с координатами, затем расписание по часам. Каждый результат
                проверяется — если в дне набралось шесть часов дороги, вы узнаете об этом до
                поездки, а не в ней.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  'Только бесплатные модели — за генерацию вы не платите',
                  'Сначала черновик, применяете вы — ничего не перезаписывается молча',
                  'Можно достроить один день, не трогая остальные',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sand-300">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-clay-400" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <ButtonLink to="/register" size="lg">
                  Попробовать
                </ButtonLink>
              </div>
            </div>
          </Reveal>

          <div ref={ref}>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
                <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                  <span className="size-2.5 rounded-full bg-red-400/70" />
                  <span className="size-2.5 rounded-full bg-sun-400/70" />
                  <span className="size-2.5 rounded-full bg-emerald-400/70" />
                  <span className="ml-2 text-xs text-sand-400">Новое путешествие</span>
                </div>

                <div className="p-5">
                  <div className="rounded-xl border border-white/10 bg-black/25 p-4">
                    <div className="mb-2 text-xs text-sand-400">Ваш запрос</div>
                    <p className="min-h-[3.25rem] text-[0.95rem] leading-relaxed">
                      {typed}
                      {!reduced && typed.length < PROMPT.length ? (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity }}
                          className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 bg-clay-400"
                        />
                      ) : null}
                    </p>
                  </div>

                  <AnimatePresence>
                    {thinking ? (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex items-center gap-2 overflow-hidden px-1 pt-4 text-sm text-sand-400"
                      >
                        {[0, 1, 2].map((i) => (
                          <motion.span
                            key={i}
                            animate={{ y: [0, -4, 0] }}
                            transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.13 }}
                            className="size-1.5 rounded-full bg-clay-400"
                          />
                        ))}
                        <span className="ml-1">собираю маршрут…</span>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>

                  <ul className="mt-4 space-y-2">
                    {GENERATED.slice(0, visibleDays).map((item) => (
                      <motion.li
                        key={item.day}
                        initial={reduced ? false : { opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: DURATION.base, ease: EASE_OUT_EXPO }}
                        className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3"
                      >
                        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-clay-500/20 font-mono text-xs text-clay-300">
                          {item.day}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm leading-snug">{item.title}</span>
                          <span className="mt-0.5 block text-xs text-sand-400">{item.tag}</span>
                        </span>
                      </motion.li>
                    ))}
                  </ul>

                  {visibleDays >= GENERATED.length ? (
                    <motion.div
                      initial={reduced ? false : { opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="mt-4 flex items-center justify-between rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300"
                    >
                      <span>Черновик готов · 11 дней, 34 точки</span>
                      <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs">
                        Применить
                      </span>
                    </motion.div>
                  ) : null}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
